import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Form,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  Radio,
  Space,
  InputNumber,
  Layout,
} from 'antd';
import { Link } from 'react-router-dom';

import BreadcrumbContainer from '../../containers/Breadcrumb';
import CustomButton from '../../components/CustomButton';
import SelectOrganizationsActive from '../../components/SelectOrganizationsActive';
import INTERNAL_LINKS from '../../enums/InternalLinks';

import { getAll as getCurrencyTypes } from '../../api/module/currency_types';
import { query as getAllCostCenter } from '../../api/module/costs';

import './style.scss';
import { catchExceptionSRate } from '../../utils/helpers/helpers';

const { Title, Paragraph } = Typography;
const breadcrumbItems = [
  {
    text: 'Proyectos',
    to: `${INTERNAL_LINKS.PROJECTS}`,
  },
  {
    text: 'Formulario de Proyecto',
  },
];
const { Option } = Select;
const { Content } = Layout;

const validateMessages = {
  required: '${label} Es Requerido!',
};

const config = {
  rules: [
    {
      type: 'object',
      required: true,
      message: 'Por favor seleccione el tiempo!',
    },
  ],
};

const FormProject = ({
  title,
  onFinish,
  initialValues,
  clients,
  project_id,
  form,
  readOnly,
  setPendingChanges,
  templateInfo,
}) => {
  const [currencyTypesOptions, setCurrencyTypesOptions] = useState({
    list: [],
    COP: null,
  });
  const [initialDate, setInitialDate] = useState(null);
  const [finishDate, setFinishDate] = useState(null);
  const [costCenters, setCostCenters] = useState([]);

  const getCurrencyTypesOptions = async () => {
    const response = await getCurrencyTypes();
    setCurrencyTypesOptions({
      list: response.data.results,
      COP: response.data.results.find((item) => item.sigla === 'COP'),
    });
  };

  useEffect(() => {
    getCurrencyTypesOptions();
    if (initialValues.fecha_inicio) {
      setInitialDate(initialValues.fecha_inicio);
    }
    if (initialValues.fecha_fin) {
      setFinishDate(initialValues.fecha_fin);
    }
    if (initialValues.centro_costo_cliente) {
      clientChangeEvent(initialValues.centro_costo_cliente);
    }
  }, []);

  const clientChangeEvent = async (value) => {
    try {
      const response = await getAllCostCenter(
        `?filter[cliente_centro_costo]=${value}&page[size]=1000`,
      );
      setCostCenters(response.data.results);
    } catch (error) {
      catchExceptionSRate(error);
    }
  };

  return (
    <Content>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        {title}
      </Title>
      {!readOnly && (
        <Paragraph>
          A continuación, diligencie el formulario "{title}".
          <br />
          Recuerde que los campos obligatorios están marcados con asterisco (*).
        </Paragraph>
      )}
      <Form
        form={form}
        name="nest-messages"
        layout="vertical"
        onFinish={(data) => onFinish(data)}
        validateMessages={validateMessages}
        initialValues={initialValues}
        autoComplete="off"
        labelCol={{
          span: 24,
        }}
        wrapperCol={{
          span: 24,
        }}
        labelAlign="left"
        onValuesChange={() => setPendingChanges(true)}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="tipo_proyecto"
              label="Tipo de proyecto"
              tooltip={{
                placement: 'top',
                title: `Elija el tipo de proyecto`,
              }}
            >
              <Radio.Group disabled={readOnly || project_id > 0}>
                <Radio value={1}>Regular</Radio>
                <Radio value={0}>Tipo ICFES</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="tipos_proceso" label="Tipos de proceso">
              <Select
                options={templateInfo.process_types.map((s) => ({
                  ...s,
                  ...(s.value === 1 ? { disabled: true } : {}),
                }))}
                mode="tags"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item noStyle dependencies={['tipos_proceso']}>
              {({ getFieldValue }) => {
                return (
                  <Form.Item name="tipos_contrato" label="Tipos de contrato">
                    <Select
                      options={getFieldValue('tipos_proceso')
                        .reduce((pv, cv) => {
                          return [
                            ...pv,
                            ...(templateInfo.contract_types[cv] || []),
                          ];
                        }, [])
                        .map((s) => ({
                          ...s,
                          ...(s.value === 1 ? { disabled: true } : {}),
                        }))}
                      mode="tags"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="cliente"
              label="Cliente - Aliado"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Cliente..."
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                disabled={readOnly}
                onChange={(value) => {
                  let client_centro_costo = 0;
                  for (let item of clients) {
                    if (item.id === value) {
                      client_centro_costo = item.centro_costo_cliente;
                    }
                  }
                  clientChangeEvent(client_centro_costo);
                }}
              >
                {clients != null && (
                  <>
                    {clients.map((item) => (
                      <Option key={`key-client-${item.id}`} value={item.id}>
                        {item.nombre}
                      </Option>
                    ))}
                  </>
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="organizacion"
              label="Organización ejecutora"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <SelectOrganizationsActive
                organization={initialValues.organizacion}
                disabled={readOnly}
                organization_name={initialValues.organizacion_nombre}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="nombre"
              label="Nombre del proyecto"
              rules={[
                {
                  required: true,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || value.length <= 100) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      'Solo puede ingresar 100 caracteres!',
                    );
                  },
                }),
              ]}
            >
              <Input
                placeholder="Nombre del Proyecto..."
                allowClear
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="tipo_contratacion"
              label="Tipo de contratación"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Tipo de contratación..."
                disabled={readOnly}
                options={[
                  { value: 0, label: 'Convenio' },
                  { value: 1, label: 'Contrato' },
                  { value: 2, label: 'Alianza' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="centro_costo"
              label="Centro de costos del proyecto"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Centro de costos del proyecto..."
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                disabled={readOnly}
              >
                {costCenters.map((item) => (
                  <Option key={`key-client-${item.id}`} value={item.id}>
                    {item.codigo + ' - ' + item.nombre}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="Código del proyecto Syscorpo"
              name="codigo_syscorpo"
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="Sigla del proyecto"
              name="nombre_syscorpo"
              rules={[
                {
                  required: true,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || value.length <= 11) {
                      return Promise.resolve();
                    }
                    return Promise.reject('Solo puede ingresar 11 caracteres!');
                  },
                }),
              ]}
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="fecha_inicio" label="Fecha de inicio" {...config}>
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                disabled={readOnly}
                onChange={(value) => {
                  setInitialDate(value);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="fecha_fin"
              label="Fecha de finalización"
              {...config}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                disabled={readOnly}
                onChange={(value) => {
                  setFinishDate(value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            {initialDate && (
              <Form.Item>
                Fecha de inicio seleccionada:{' '}
                <strong>{initialDate.format('DD MMMM YYYY')}</strong>
              </Form.Item>
            )}
          </Col>
          <Col span={12}>
            {finishDate && (
              <Form.Item>
                Fecha de finalización seleccionada:{' '}
                <strong>{finishDate.format('DD MMMM YYYY')}</strong>
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="tipo_moneda"
              label="Seleccione la moneda origen del proyecto"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Moneda..."
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                disabled={readOnly}
              >
                {currencyTypesOptions &&
                  currencyTypesOptions.list.map((item) => (
                    <Option key={`currency-key-${item.id}`} value={item.id}>
                      {item.sigla}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            {currencyTypesOptions.COP && (
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.tipo_moneda !== currentValues.tipo_moneda
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('tipo_moneda') !==
                  currencyTypesOptions.COP.id ? (
                    <Form.Item
                      name="trp"
                      label="Tasa representativa del mercado para el proyecto (TRM)"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="TRP..."
                        style={{ width: '100%' }}
                        allowClear
                        min={0}
                        formatter={(value) =>
                          `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                        disabled={readOnly}
                      />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="pagos_tope"
              label="¿Tiene contratos con pagos variables sin tope?"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Radio.Group disabled={readOnly}>
                <Radio value={false}>Sí</Radio>
                <Radio value={true}>No</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="costos_directos"
          label="Costos directos"
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                if (value < 0) {
                  return Promise.reject(new Error('Debe ser positivo'));
                }
                if (
                  value.toString().includes('.') &&
                  value.toString().split('.', 2)[1].length > 2
                ) {
                  return Promise.reject(
                    new Error('Debe ser un valor con máximo dos decimales'),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          labelCol={{
            span: 4,
          }}
          wrapperCol={{
            span: 14,
          }}
        >
          <InputNumber
            style={{ width: '100%' }}
            disabled={readOnly}
            formatter={(value) =>
              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>
        <Form.Item
          name="gastos_administrativos"
          label="Gastos administrativos"
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                if (value < 0) {
                  return Promise.reject(new Error('Debe ser positivo'));
                }
                if (
                  value.toString().includes('.') &&
                  value.toString().split('.', 2)[1].length > 2
                ) {
                  return Promise.reject(
                    new Error('Debe ser un valor con máximo dos decimales'),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          labelCol={{
            span: 4,
          }}
          wrapperCol={{
            span: 14,
          }}
        >
          <InputNumber
            style={{ width: '100%' }}
            disabled={readOnly}
            formatter={(value) =>
              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>
        <Form.Item
          name="contrapartida"
          label="Contrapartida"
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                if (value < 0) {
                  return Promise.reject(new Error('Debe ser positivo'));
                }
                if (
                  value.toString().includes('.') &&
                  value.toString().split('.', 2)[1].length > 2
                ) {
                  return Promise.reject(
                    new Error('Debe ser un valor con máximo dos decimales'),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          labelCol={{
            span: 4,
          }}
          wrapperCol={{
            span: 14,
          }}
        >
          <InputNumber
            style={{ width: '100%' }}
            disabled={readOnly}
            formatter={(value) =>
              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>
        <Form.Item
          name="iva"
          label="IVA"
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                if (value < 0) {
                  return Promise.reject(new Error('Debe ser positivo'));
                }
                if (
                  value.toString().includes('.') &&
                  value.toString().split('.', 2)[1].length > 2
                ) {
                  return Promise.reject(
                    new Error('Debe ser un valor con máximo dos decimales'),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          labelCol={{
            span: 4,
          }}
          wrapperCol={{
            span: 14,
          }}
        >
          <InputNumber
            style={{ width: '100%' }}
            disabled={readOnly}
            formatter={(value) =>
              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>
        <Form.Item
          noStyle
          dependencies={[
            'costos_directos',
            'gastos_administrativos',
            'contrapartida',
            'iva',
          ]}
        >
          {({ getFieldValue }) => (
            <Row align="middle" style={{ marginBottom: '10px' }}>
              <Col span={24}>Valor total del proyecto (presupuesto): </Col>
              <Col span={14}>
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  value={(
                    (getFieldValue('costos_directos') || 0) +
                    (getFieldValue('gastos_administrativos') || 0) +
                    (getFieldValue('contrapartida') || 0) +
                    (getFieldValue('iva') || 0)
                  ).toFixed(2)}
                  disabled
                />
              </Col>
            </Row>
          )}
        </Form.Item>
        <Form.Item
          name="plazo_terminacion"
          label="Plazo de terminación y liquidación con el aliado"
          tooltip={{
            placement: 'top',
            title:
              'Estos días serán contados a partir de la fecha de finalización del contrato con el aliado incluidas todas sus modificaciones',
          }}
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
            {
              validator: (_, value) => {
                if (!value && value !== 0) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                if (value < 30 || value > 120) {
                  return Promise.reject(
                    new Error('Debe ser un número entre 30 y 120'),
                  );
                }
                if (value.toString().includes('.')) {
                  return Promise.reject(new Error('Debe ser un valor entero'));
                }

                return Promise.resolve();
              },
            },
          ]}
          labelCol={{
            span: 14,
          }}
          wrapperCol={{
            span: 4,
          }}
        >
          <InputNumber style={{ width: '100%' }} disabled={readOnly} />
        </Form.Item>
        <Form.Item
          name="plazo_interno"
          label="Plazo interno máximo para elaborar actas de terminación y liquidación con los contratistas"
          tooltip={{
            placement: 'top',
            title:
              'El Área jurídica recomienda que el plazo máximo de firma de acta de los contratistas sea igual o inferior a la mitad del plazo de liquidación con el aliado',
          }}
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
            ({ getFieldValue }) => ({
              validator: (_, value) => {
                if (!value && value !== 0) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                const pt = getFieldValue('plazo_terminacion');
                if (pt && value > pt - 15) {
                  return Promise.reject(
                    new Error(
                      'El plazo interno no debe ser mayor al Plazo de terminación - 15 (días)',
                    ),
                  );
                }
                if (value < 15 || value > 105) {
                  return Promise.reject(
                    new Error(
                      'El plazo interno máximo para elaborar actas de terminación debe ser mínimo de 15 días y máximo 15 días antes del plazo de terminación con el aliado',
                    ),
                  );
                }
                if (value.toString().includes('.')) {
                  return Promise.reject(new Error('Debe ser un valor entero'));
                }

                return Promise.resolve();
              },
            }),
          ]}
          labelCol={{
            span: 14,
          }}
          wrapperCol={{
            span: 4,
          }}
        >
          <InputNumber style={{ width: '100%' }} disabled={readOnly} />
        </Form.Item>
      </Form>
    </Content>
  );
};

FormProject.propTypes = {
  title: PropTypes.string,
  onFinish: PropTypes.func,
  initialValues: PropTypes.object,
  client: PropTypes.array,
  organization: PropTypes.array,
  readOnly: PropTypes.bool,
  setPendingChanges: PropTypes.func,
  templateInfo: PropTypes.object,
};

FormProject.defaultProps = {
  title: '',
  onFinish: () => {},
  initialValues: {},
  client: [],
  organization: [],
  readOnly: false,
  templateInfo: {},
};

export default FormProject;
